import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from './lib/ga-service';
import Projects from './pages/Projects';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import MyFirstGame from './pages/MyFirstGame';
import Courses from './pages/Courses';
import PassCodeur from './pages/PassCodeur';
import PassCodeurSuccess from './pages/PassCodeurSuccess';
import CGV from './pages/CGV';

import Play from './pages/Play';
import { Toaster } from './components/ui/toaster';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import CourseProtectedRoute from './components/CourseProtectedRoute';
import { AuthProvider, useAuth } from './lib/auth-context';

import { ThemeProvider } from './lib/theme-provider';
import { PassCodeurProvider } from './lib/pass-codeur-context';
import { CookieConsentProvider } from './lib/cookie-consent-context';
import CookieBanner from './components/CookieBanner';

// baseprogrammation
import Baseprogrammation from './pages/cours/baseprogrammation';
import CourseContainerBaseprogrammation from './pages/cours/baseprogrammation/CourseContainer';
// ameliorevisée
import Ameliorevisée from './pages/cours/ameliorevisée';
import CourseContainerAmeliorevisée from './pages/cours/ameliorevisée/CourseContainer';
// morpion
import Morpion from './pages/cours/morpion';
import CourseContainerMorpion from './pages/cours/morpion/CourseContainer';
// flappybird
import Flappybird from './pages/cours/flappybird';
import CourseContainerFlappybird from './pages/cours/flappybird/CourseContainer';
// snake
import Snake from './pages/cours/snake';
import CourseContainerSnake from './pages/cours/snake/CourseContainer';

// Composant pour tracker les pages vues
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Tracker chaque changement de page
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
};

// Composant pour décider si le footer doit être affiché
const ConditionalFooter = () => {
  const location = useLocation();
  
  // Ne pas afficher le footer sur la page des projets et ses sous-routes,
  // sur les pages de jeu, certains exercices et toutes les pages de cours
  if (location.pathname.startsWith('/project') || 
      location.pathname.startsWith('/play/') || 
      location.pathname === '/exercice2' ||
      location.pathname === '/exercice1' ||
      location.pathname.startsWith('/cours/')) {
    return null;
  }
  
  return <Footer />;
};

const AppContent = () => {
  const { currentUser, userData, updateUserPreferences } = useAuth();
  
  return (
    <ThemeProvider 
      currentUser={currentUser}
      userData={userData}
      updateUserPreferences={updateUserPreferences}
    >
      <PassCodeurProvider>
        <CookieConsentProvider>
          <Router future={{ 
            v7_startTransition: true,
            v7_relativeSplatPath: true 
          }}>
            <AnalyticsTracker />
            <div className="min-h-screen text-foreground relative flex flex-col">
              {/* Éléments décoratifs du fond */}
              <div className="floating-bg-elements"></div>
              
              <Navbar />
              <div className="relative flex-grow">
                <main className="overflow-y-auto pt-4">
                  <Routes>
                    {/* Page d'accueil publique */}
                    <Route path="/" element={<MyFirstGame />} />
                    
                    {/* Routes publiques */}
                    <Route path="/login" element={<div className="px-6"><Login /></div>} />
                    <Route path="/register" element={<div className="px-6"><Register /></div>} />
                    <Route path="/forgot-password" element={<div className="px-6"><ForgotPassword /></div>} />
                    <Route path="/courses" element={<div className="px-6"><Courses /></div>} />
                    <Route path="/cgv" element={<div className="px-6"><CGV /></div>} />
                    
                    {/* Routes pour la page Play (publique) */}
                    <Route path="/play" element={<Play />} />
                    <Route path="/play/:alias" element={<Play />} />
                    <Route path="/play/:alias/:projectId" element={<Play />} />
                    
                    {/* Routes Projects - accessibles aux utilisateurs non connectés */}
                    <Route path="/project" element={<Projects />} />
                    <Route path="/project/:alias" element={<Projects />} />
                    <Route path="/project/:alias/:projectId" element={<Projects />} />
                    
                    {/* Routes protégées */}
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <div className="px-6"><Profile /></div>
                      </ProtectedRoute>
                    } />
                    <Route path="/pass-codeur" element={<div className="px-6"><PassCodeur /></div>} />
                    <Route path="/pass-codeur/success" element={
                      <ProtectedRoute>
                        <div className="px-6"><PassCodeurSuccess /></div>
                      </ProtectedRoute>
                    } />

                    
                    {/* Routes pour le cours Les bases de la Programmation */}
                    <Route path="/cours/baseprogrammation" element={
                      <Baseprogrammation />
                    } />
                    <Route path="/cours/baseprogrammation/:chapitreId" element={
                      <CourseProtectedRoute>
                        <CourseContainerBaseprogrammation />
                      </CourseProtectedRoute>
                    } />
                    {/* Routes pour le cours Jeu 1 : Améliore ta visée */}
                    <Route path="/cours/ameliorevisée" element={
                      <Ameliorevisée />
                    } />
                    <Route path="/cours/ameliorevisée/:chapitreId" element={
                      <CourseProtectedRoute>
                        <CourseContainerAmeliorevisée />
                      </CourseProtectedRoute>
                    } />
                    {/* Routes pour le cours Jeu 2 : Morpion */}
                    <Route path="/cours/morpion" element={
                      <Morpion />
                    } />
                    <Route path="/cours/morpion/:chapitreId" element={
                      <CourseProtectedRoute>
                        <CourseContainerMorpion />
                      </CourseProtectedRoute>
                    } />
                    {/* Routes pour le cours Jeu 3 : Flappy Bird */}
                    <Route path="/cours/flappybird" element={
                      <Flappybird />
                    } />
                    <Route path="/cours/flappybird/:chapitreId" element={
                      <CourseProtectedRoute>
                        <CourseContainerFlappybird />
                      </CourseProtectedRoute>
                    } />
                    {/* Routes pour le cours Snake */}
                    <Route path="/cours/snake" element={
                      <Snake />
                    } />
                    <Route path="/cours/snake/:chapitreId" element={
                      <CourseProtectedRoute>
                        <CourseContainerSnake />
                      </CourseProtectedRoute>
                    } />
                    
                    {/* Route par défaut */}
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </main>
              </div>
              <ConditionalFooter />
              
              {/* Banner de consentement aux cookies */}
              <CookieBanner />
              
              <Toaster />
            </div>
          </Router>
        </CookieConsentProvider>
      </PassCodeurProvider>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;